import {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";

export type SignaturePadHandle = {
  clear: () => void;
  isEmpty: () => boolean;
  toDataURL: () => string;
};

type Props = {
  height?: number;
  className?: string;
  onInkChange?: (hasInk: boolean) => void;
};

/**
 * Minimal pointer-event-based signature canvas. No external deps —
 * draws strokes via mouse / touch / pen, exports as base64 PNG.
 */
export const SignaturePad = forwardRef<SignaturePadHandle, Props>(
  function SignaturePad({ height = 220, className, onInkChange }, ref) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const isDrawing = useRef(false);
    const last = useRef<{ x: number; y: number } | null>(null);
    const [hasInk, setHasInk] = useState(false);
    const onInkChangeRef = useRef(onInkChange);
    onInkChangeRef.current = onInkChange;

    useEffect(() => {
      onInkChangeRef.current?.(hasInk);
    }, [hasInk]);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Initial sizing. Subsequent resizes capture the current pixels,
      // re-size the canvas, and redraw — assigning to canvas.width/height
      // is destructive, so re-doing it on every resize was wiping the
      // user's signature (e.g. the keyboard collapsing on submit triggers
      // a viewport resize on mobile Safari).
      const sizeFor = (width: number, height: number) => {
        const dpr = window.devicePixelRatio || 1;
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.scale(dpr, dpr);
        ctx.strokeStyle = "#0f172a";
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
      };

      const initialRect = canvas.getBoundingClientRect();
      sizeFor(initialRect.width, initialRect.height);

      const onResize = () => {
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        if (
          canvas.width === rect.width * dpr &&
          canvas.height === rect.height * dpr
        ) {
          return;
        }
        // Snapshot existing strokes and restore them after the resize.
        let snapshot: HTMLImageElement | null = null;
        try {
          const dataUrl = canvas.toDataURL("image/png");
          if (dataUrl && !dataUrl.endsWith("AAAASUVORK5CYII=")) {
            snapshot = new Image();
            snapshot.src = dataUrl;
          }
        } catch {
          // Cross-origin tainted — give up on the restore.
        }
        sizeFor(rect.width, rect.height);
        if (snapshot) {
          const img = snapshot;
          const draw = () =>
            canvas
              .getContext("2d")
              ?.drawImage(img, 0, 0, rect.width, rect.height);
          if (img.complete) draw();
          else img.onload = draw;
        }
      };
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          const canvas = canvasRef.current;
          if (!canvas) return;
          const ctx = canvas.getContext("2d");
          if (!ctx) return;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          setHasInk(false);
        },
        isEmpty: () => !hasInk,
        toDataURL: () => canvasRef.current?.toDataURL("image/png") ?? "",
      }),
      [hasInk]
    );

    const getPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
      const rect = (
        e.currentTarget as HTMLCanvasElement
      ).getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
      (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
      isDrawing.current = true;
      last.current = getPos(e);
    };

    const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawing.current) return;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx || !last.current) return;
      const p = getPos(e);
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      last.current = p;
      if (!hasInk) setHasInk(true);
    };

    const end = (e: React.PointerEvent<HTMLCanvasElement>) => {
      (e.currentTarget as HTMLCanvasElement).releasePointerCapture(e.pointerId);
      isDrawing.current = false;
      last.current = null;
    };

    return (
      <canvas
        ref={canvasRef}
        style={{ height, touchAction: "none" }}
        className={`w-full rounded-2xl border border-neutral-200 bg-white ${className ?? ""}`}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
      />
    );
  }
);
